import { Box } from "@mui/material";
import React from "react";
import { COLORS } from "../../assets/theme";
import CustomTextField from "../../components/CustomTextField";
import CustomButton from "../../components/CustomButton";
import CustomSelect from "../../components/CustomSelect";
import CustomLabel from "../../components/CustomLabel";
import {
  getAdvancedSettings,
  updateSettings,
} from "../../services/AdvancedSettings";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAllBranches } from "../../services/BranchesService";
import { toast } from "react-toastify";

const ADVANCED_SETTINGS_TOAST_ID = "advanced-settings-toast";
const REMINDER_24H_TEMPLATE =
  "Dear (client_name), please don't forget your appointment at (email_sender) in (branch_name) on (start_date). Thank you";
const REMINDER_2H_TEMPLATE =
  "Dear (client_name), please don't forget your appointment at (email_sender) in (branch_name) after two hours from now. Thank you";
const CREATE_APPOINTMENT_TEMPLATE =
  "Dear (client_name), your appointment at (email_sender) in (branch_name) has been set on (start_date) at (time_start) until (time_end). Thank you";
const CANCEL_APPOINTMENT_TEMPLATE =
  "Dear (client_name), your appointment at (email_sender) in (branch_name) on (start_date) has been cancelled. Thank you";
const EDIT_APPOINTMENT_TEMPLATE =
  "Dear (client_name), your appointment at (email_sender) in (branch_name) has been re-scheduled on (start_date) at (time_start) until (time_end). Thank you";

const SLOT_DURATION_OPTIONS = [15, 30, 60];

const withTemplateFallback = (
  value: string | number | null | undefined,
  template: string
): string => {
  if (value === null || value === undefined) {
    return template;
  }
  const asString = String(value).trim();
  return asString ? asString : template;
};

const toTimeInputValue = (
  value: number | string | null | undefined
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return "";
    }
    if (trimmed.includes(":")) {
      const [rawHours = "00", rawMinutes = "00"] = trimmed.split(":");
      const hours = rawHours.padStart(2, "0").slice(-2);
      const minutes = rawMinutes.padStart(2, "0").slice(0, 2);
      return `${hours}:${minutes}`;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) {
      return "";
    }
    return toTimeInputValue(parsed);
  }

  const normalized = Math.max(0, value);
  if (normalized >= 24) {
    return "23:59";
  }

  let hours = Math.floor(normalized);
  let minutes = Math.round((normalized - hours) * 60);

  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  const prefixedHours = hours.toString().padStart(2, "0");
  const prefixedMinutes = minutes.toString().padStart(2, "0");
  return `${prefixedHours}:${prefixedMinutes}`;
};

const isFullDayValue = (
  value: number | string | null | undefined
): boolean => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "number") {
    return value >= 24;
  }
  const trimmed = value.trim();
  if (trimmed.includes(":")) {
    const [hoursStr = "0", minutesStr = "0"] = trimmed.split(":");
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return false;
    }
    return hours > 23 || (hours === 23 && minutes === 59);
  }
  const parsed = Number(trimmed);
  return !Number.isNaN(parsed) && parsed >= 24;
};

const fromTimeInputValue = (
  value: string,
  treatMaxAsFullDay = false
): string => {
  if (!value) {
    return "0";
  }
  const [hoursStr = "0", minutesStr = "0"] = value.split(":");
  const hours = Number(hoursStr);
  const minutes = Number(minutesStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return "0";
  }
  if (treatMaxAsFullDay && hours === 23 && minutes === 59) {
    return "24";
  }
  const numericValue = hours + minutes / 60;
  return Number.isInteger(numericValue)
    ? `${numericValue}`
    : numericValue.toString();
};

const extractMobileDynamicMessage = (value: string): string => {
  const match = value.match(/\bwts\s*=\s*(\d+)/i);
  return match ? match[1] : "";
};

const Advanced: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [workTimeEnd, setWorkTimeEnd] = React.useState<string>(
    toTimeInputValue(24)
  );
  const [workTimeStart, setWorkTimeStart] = React.useState<string>(
    toTimeInputValue(6)
  );
  const [isWorkTimeEndFullDay, setIsWorkTimeEndFullDay] =
    React.useState<boolean>(true);
  const [isWorkTimeStartFullDay, setIsWorkTimeStartFullDay] =
    React.useState<boolean>(false);
  const [timeFrame, setTimeFrame] = React.useState<number | string>(60);
  const [slotDuration, setSlotDuration] = React.useState<number | string>(
    SLOT_DURATION_OPTIONS[1]
  );
  const [selectedBranch, setSelectedBranch] = React.useState<string | number>(
    ""
  );
  const [branches, setBranches] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [settings, setSettings] = React.useState<any[]>([]);
  const [venueId, setVenueId] = React.useState<string | null>(() => {
    return location.state?.venueId || params.branchId || null;
  });

  React.useEffect(() => {
    const fetchBranches = async () => {
      try {
        if (!venueId) {
          console.warn("No venueId available, skipping branch fetch");
          setBranches([]);
          return;
        }
        setLoading(true);
        const response = await getAllBranches(venueId);

        let branchesData = [];
        if (response && response.businesses) {
          branchesData = response.businesses;
        } else if (response && response.data) {
          branchesData = response.data;
        } else if (Array.isArray(response)) {
          branchesData = response;
        }

        const branches = Array.isArray(branchesData) ? branchesData : [];
        setBranches(branches);
        if (branches.length > 0 && !selectedBranch) {
          setSelectedBranch(branches[0].id);
        }
      } catch (error) {
        console.error("Failed to fetch branches:", error);
        setBranches([]);
        toast.error("Failed to fetch branches.", { position: "top-right" });
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, [venueId]);

  React.useEffect(() => {
    if (selectedBranch) {
      setWorkTimeEnd(toTimeInputValue(24));
      setIsWorkTimeEndFullDay(true);
      setWorkTimeStart(toTimeInputValue(6));
      setIsWorkTimeStartFullDay(false);
      setTimeFrame(60);
      setSlotDuration(SLOT_DURATION_OPTIONS[1]);
      setReminder24h(REMINDER_24H_TEMPLATE);
      setReminder2h(REMINDER_2H_TEMPLATE);
      setCreateAppointmentMsg(CREATE_APPOINTMENT_TEMPLATE);
      setEditAppointmentMsg(EDIT_APPOINTMENT_TEMPLATE);
      setCancelAppointmentMsg(CANCEL_APPOINTMENT_TEMPLATE);
      setCountryCode("");
      setDynamicMessage("");
      setMobileDynamicMessage("");
      setIsMobileDynamicMessageManual(false);
    }
    const fetchSettings = async () => {
      if (!selectedBranch) return;
      try {
        setLoading(true);
        const response = await getAdvancedSettings(selectedBranch.toString());
        const fetchedSettings = response.settings || [];
        setSettings(fetchedSettings);
        fetchedSettings.forEach((setting: any) => {
          const { label, value } = setting;
          switch (label) {
            case "calendar_start":
              if (value !== null && value !== undefined) {
                setIsWorkTimeStartFullDay(isFullDayValue(value));
                setWorkTimeStart(toTimeInputValue(value));
              }
              break;
            case "calendar_end":
              if (value !== null && value !== undefined) {
                setIsWorkTimeEndFullDay(isFullDayValue(value));
                setWorkTimeEnd(toTimeInputValue(value));
              }
              break;
            case "time frame":
              if (value !== null && value !== undefined) {
                setTimeFrame(value);
              }
              break;
            case "slot duration":
              if (value !== null && value !== undefined) {
                const numericDuration = Number(value);
                const safeDuration = SLOT_DURATION_OPTIONS.includes(
                  numericDuration
                )
                  ? numericDuration
                  : SLOT_DURATION_OPTIONS[1];
                setSlotDuration(safeDuration);
              }
              break;
            case "24 hours":
              setReminder24h(
                withTemplateFallback(value, REMINDER_24H_TEMPLATE)
              );
              break;
            case "2 hours":
              setReminder2h(withTemplateFallback(value, REMINDER_2H_TEMPLATE));
              break;
            case "create appointment":
              setCreateAppointmentMsg(
                withTemplateFallback(value, CREATE_APPOINTMENT_TEMPLATE)
              );
              break;
            case "edit appointment":
              setEditAppointmentMsg(
                withTemplateFallback(value, EDIT_APPOINTMENT_TEMPLATE)
              );
              break;
            case "cancel appointment":
              setCancelAppointmentMsg(
                withTemplateFallback(value, CANCEL_APPOINTMENT_TEMPLATE)
              );
              break;
            case "country code":
              if (value !== null && value !== undefined) {
                setCountryCode(value);
              }
              break;
            case "dynamic_message":
              if (value !== null && value !== undefined) {
                setDynamicMessage(String(value));
              }
              break;
            case "mobile_dynamic_message":
              if (value !== null && value !== undefined) {
                setMobileDynamicMessage(String(value));
              }
              break;
            default:
              break;
          }
        });
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        toast.error("Failed to fetch settings.", { position: "top-right" });
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [selectedBranch]);

  const [reminder24h, setReminder24h] = React.useState<string>(
    REMINDER_24H_TEMPLATE
  );
  const [reminder2h, setReminder2h] = React.useState<string>(
    REMINDER_2H_TEMPLATE
  );
  const [createAppointmentMsg, setCreateAppointmentMsg] =
    React.useState<string>(CREATE_APPOINTMENT_TEMPLATE);
  const [editAppointmentMsg, setEditAppointmentMsg] =
    React.useState<string>(EDIT_APPOINTMENT_TEMPLATE);
  const [cancelAppointmentMsg, setCancelAppointmentMsg] =
    React.useState<string>(CANCEL_APPOINTMENT_TEMPLATE);
  const [countryCode, setCountryCode] = React.useState<string>("");
  const [dynamicMessage, setDynamicMessage] = React.useState<string>("");
  const [mobileDynamicMessage, setMobileDynamicMessage] =
    React.useState<string>("");
  const [isMobileDynamicMessageManual, setIsMobileDynamicMessageManual] =
    React.useState<boolean>(false);

  const handleDynamicMessageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.value;
    setDynamicMessage(nextValue);
    if (!isMobileDynamicMessageManual) {
      const extracted = extractMobileDynamicMessage(nextValue);
      if (extracted) {
        setMobileDynamicMessage(extracted);
      }
    }
  };

  const handleMobileDynamicMessageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.value;
    setMobileDynamicMessage(nextValue);
    setIsMobileDynamicMessageManual(nextValue.trim().length > 0);
  };

  const handleSave = async () => {
    if (!selectedBranch) {
      console.error("No branch selected");
      toast.error("Please select a branch before saving.", {
        position: "top-right",
      });
      return;
    }
    const payload: any = {};
    settings.forEach((setting) => {
      let value: string = "";
      switch (setting.label) {
        case "calendar_start":
          value = fromTimeInputValue(workTimeStart, isWorkTimeStartFullDay);
          break;
        case "calendar_end":
          value = fromTimeInputValue(workTimeEnd, isWorkTimeEndFullDay);
          break;
        case "time frame":
          value = timeFrame.toString();
          break;
        case "slot duration":
          value = slotDuration.toString();
          break;
        case "24 hours":
          value = reminder24h;
          break;
        case "2 hours":
          value = reminder2h;
          break;
        case "create appointment":
          value = createAppointmentMsg;
          break;
        case "edit appointment":
          value = editAppointmentMsg;
          break;
        case "cancel appointment":
          value = cancelAppointmentMsg;
          break;
        case "country code":
          value = countryCode;
          break;
        case "dynamic_message":
          value = dynamicMessage;
          break;
        case "mobile_dynamic_message":
          value = mobileDynamicMessage;
          break;
        default:
          break;
      }
      payload[setting.label] = { settings_id: setting.settingsId, value };
    });
    toast.dismiss(ADVANCED_SETTINGS_TOAST_ID);
    const savingToastId = toast.loading("Saving advanced settings...", {
      position: "top-right",
      toastId: ADVANCED_SETTINGS_TOAST_ID,
      closeOnClick: false,
      autoClose: false,
    });
    try {
      setLoading(true);
      await updateSettings(selectedBranch.toString(), payload);
      toast.update(savingToastId, {
        render: "Advanced settings updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
      console.log("Settings updated successfully");
    } catch (error) {
      console.error("Failed to update settings:", error);
      toast.update(savingToastId, {
        render: "Failed to update settings. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 4000,
        closeOnClick: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ px: 2, pt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: { xs: "100%", sm: "100%" },
            p: { xs: 2, sm: 3 },
            pt: { xs: 4, sm: 5 },
            boxShadow: 2,
            borderRadius: 2,
            bgcolor: COLORS.WhiteBackground,
            position: "relative",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <CustomLabel
              text="Select Branch"
              sx={{ mb: 1, fontSize: "0.95rem" }}
            />
            <CustomSelect
              value={selectedBranch}
              options={branches.map((branch) => ({
                label: branch.name || branch.title,
                value: branch.id,
              }))}
              onChange={(value) => setSelectedBranch(value as string | number)}
              fullWidth
              disabled={loading}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <CustomTextField
              label={"Work Time (Start)"}
              variant="outlined"
              fullWidth
              type="time"
              value={workTimeStart}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const nextValue = e.target.value;
                setWorkTimeStart(nextValue);
                setIsWorkTimeStartFullDay(nextValue === "23:59");
              }}
              inputProps={{ min: "00:00", max: "23:59", step: 60 }}
            />
            <CustomTextField
              label={"Work Time (End)"}
              variant="outlined"
              fullWidth
              type="time"
              value={workTimeEnd}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const nextValue = e.target.value;
                setWorkTimeEnd(nextValue);
                setIsWorkTimeEndFullDay(nextValue === "23:59");
              }}
              inputProps={{ min: "00:00", max: "23:59", step: 60 }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <CustomLabel
                text="Slot Duration"
                sx={{ mb: 0, fontSize: "0.95rem" ,marginBottom: '-4px'}}
              />
              <CustomSelect
                label="Slot Duration"
                value={slotDuration}
                options={SLOT_DURATION_OPTIONS.map((minutes) => ({
                  label: `${minutes} minutes`,
                  value: minutes,
                }))}
                onChange={(value) => setSlotDuration(Number(value))}
                fullWidth
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <CustomTextField
              label={"Message in court"}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={dynamicMessage}
              onChange={handleDynamicMessageChange}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <CustomTextField
              label={"Wts number in the message"}
              variant="outlined"
              fullWidth
              type="tel"
              placeholder="e.g. 707987798"
              value={mobileDynamicMessage}
              onChange={handleMobileDynamicMessageChange}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <CustomTextField
              label={"Reminder Message before 24 hours"}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              placeholder={REMINDER_24H_TEMPLATE}
              value={reminder24h}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setReminder24h(e.target.value)
              }
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <CustomTextField
              label={"Reminder Message before 2 hours"}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              placeholder={REMINDER_2H_TEMPLATE}
              value={reminder2h}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setReminder2h(e.target.value)
              }
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <CustomTextField
              label={"Create Appointment"}
              variant="outlined"
              fullWidth
              multiline
              placeholder={CREATE_APPOINTMENT_TEMPLATE}
              rows={3}
              value={createAppointmentMsg}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCreateAppointmentMsg(e.target.value)
              }
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <CustomTextField
              label={"Edit Appointment"}
              variant="outlined"
              fullWidth
              multiline
              placeholder={EDIT_APPOINTMENT_TEMPLATE}
              rows={3}
              value={editAppointmentMsg}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditAppointmentMsg(e.target.value)
              }
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <CustomTextField
              label={"Cancel Appointment"}
              variant="outlined"
              fullWidth
              multiline
              placeholder={CANCEL_APPOINTMENT_TEMPLATE}
              rows={3}
              value={cancelAppointmentMsg}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCancelAppointmentMsg(e.target.value)
              }
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
            <CustomButton
              label="Save"
              variant="contained"
              onClick={handleSave}
              disabled={!selectedBranch || loading}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Advanced;
